import { Cube, Storefront } from "@phosphor-icons/react";
import { forwardRef, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Skeleton } from "../../feedback/Skeleton";

export type BusinessInfoChip = {
  label?: ReactNode;
  icon?: ReactNode;
};

export type BusinessInfoItem = {
  label: ReactNode;
  value?: ReactNode;
};

export type BusinessInfoSection = {
  title?: ReactNode;
  items: BusinessInfoItem[];
};

export type BusinessInfoDisplayCardProps = HTMLAttributes<HTMLDivElement> & {
  businessName: ReactNode;
  subtitle?: ReactNode;
  initials?: string;
  logoSrc?: string;
  logoAlt?: string;
  chips?: BusinessInfoChip[];
  sections: BusinessInfoSection[];
  background?: string;
  gradient?: string;
  headerBackground?: string;
  headerGradient?: string;
};

const defaultBusinessInfoChips: BusinessInfoChip[] = [
  { label: "30-100/ month", icon: <Cube size={14} weight="duotone" /> },
  { label: "Starting Up", icon: <Storefront size={14} weight="duotone" /> }
];

const DEFAULT_BUSINESS_INFO_SKELETON_ROWS = 6;
const skeletonValueWidthClassNames = ["bfrs-w-24", "bfrs-w-28", "bfrs-w-20", "bfrs-w-32", "bfrs-w-16", "bfrs-w-36"] as const;
const chipSkeletonWidthClassNames = ["bfrs-w-[118px]", "bfrs-w-[98px]"] as const;

function isMissingDisplayNode(node: ReactNode) {
  return node === undefined || node === null || node === false || (typeof node === "string" && node.trim().length === 0);
}

function BusinessInfoDetailSkeletonRows({ rows = DEFAULT_BUSINESS_INFO_SKELETON_ROWS }: { rows?: number }) {
  return (
    <div className="bfrs-grid bfrs-gap-[14px]" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="bfrs-grid bfrs-grid-cols-[minmax(0,92px)_auto_minmax(0,1fr)] bfrs-items-center bfrs-gap-x-6">
          <Skeleton animated={false} variant="text" className="bfrs-h-4 bfrs-w-full" />
          <Skeleton animated={false} variant="text" className="bfrs-h-4 bfrs-w-1" />
          <Skeleton animated={false} variant="text" className={cn("bfrs-h-4", skeletonValueWidthClassNames[index % skeletonValueWidthClassNames.length])} />
        </div>
      ))}
    </div>
  );
}

export const BusinessInfoDisplayCard = forwardRef<HTMLDivElement, BusinessInfoDisplayCardProps>(
  (
    {
      className,
      businessName,
      subtitle,
      initials,
      logoSrc,
      logoAlt,
      chips,
      sections,
      background,
      gradient,
      headerBackground,
      headerGradient,
      style,
      ...props
    },
    ref
  ) => {
    const avatarInitials = typeof initials === "string" && initials.trim().length > 0 ? initials.trim() : undefined;
    const hasBusinessName = !isMissingDisplayNode(businessName);
    const isBootstrapLoading = !hasBusinessName && sections.length === 0;
    const resolvedChips = chips ?? defaultBusinessInfoChips;
    const dynamicStyle = {
      "--bfrs-business-card-background": gradient ?? background ?? "var(--bfrs-gradient-business-info-card)",
      "--business-card-header-background": headerGradient ?? headerBackground ?? "var(--bfrs-gradient-business-card-header)",
      ...style
    } as CSSProperties;

    return (
      <div
        ref={ref}
        className={cn(
          "bfrs-faded-card-border bfrs-relative bfrs-w-full bfrs-max-w-[354px] bfrs-overflow-hidden bfrs-rounded-b-[10px] bfrs-rounded-t-xl bfrs-bg-[image:var(--bfrs-business-card-background)] bfrs-p-0 bfrs-shadow-card",
          className
        )}
        style={dynamicStyle}
        {...props}
      >
        <div
          className={cn(
            "bfrs-flex bfrs-min-h-[174px] bfrs-flex-col bfrs-items-center bfrs-px-6 bfrs-pt-[25px] bfrs-text-center bfrs-bg-[image:var(--business-card-header-background)]"
          )}
        >
          {isBootstrapLoading ? (
            <Skeleton animated={false} variant="avatar" className="bfrs-mb-[8px] bfrs-size-[37px] bfrs-bg-surface-card" />
          ) : logoSrc ? (
            <div className="bfrs-mb-[8px] bfrs-flex bfrs-size-[37px] bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-[18.66px] bfrs-bg-surface-card">
              <img src={logoSrc} alt={logoAlt ?? (typeof businessName === "string" ? businessName : "")} className="bfrs-size-[28px] bfrs-rounded-pill bfrs-object-cover" />
            </div>
          ) : avatarInitials ? (
            <div className="bfrs-mb-[8px] bfrs-flex bfrs-size-[37px] bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-[18.66px] bfrs-bg-surface-card">
              <span className="bfrs-text-[14.3px] bfrs-font-bold bfrs-leading-none bfrs-text-primary-bright">{avatarInitials}</span>
            </div>
          ) : (
            <Skeleton animated={false} variant="avatar" className="bfrs-mb-[8px] bfrs-size-[37px] bfrs-bg-surface-card" />
          )}
          {avatarInitials && !logoSrc && <span className="bfrs-sr-only">{avatarInitials}</span>}
          <div className="bfrs-grid bfrs-justify-items-center bfrs-gap-[2px]">
            {hasBusinessName ? (
              <h3 className="bfrs-max-w-[220px] bfrs-truncate bfrs-text-[16.667px] bfrs-font-bold bfrs-leading-tight bfrs-text-[var(--bfrs-dcl-color-business-card-text)]">{businessName}</h3>
            ) : (
              <Skeleton animated={false} variant="text" className="bfrs-h-5 bfrs-w-32 bfrs-bg-surface-card" />
            )}
            {isBootstrapLoading ? (
              <Skeleton animated={false} variant="text" className="bfrs-mt-1 bfrs-h-4 bfrs-w-40 bfrs-bg-surface-card" />
            ) : subtitle ? (
              <p className="bfrs-max-w-[240px] bfrs-truncate bfrs-text-[13.333px] bfrs-italic bfrs-leading-tight bfrs-text-[var(--bfrs-dcl-color-business-card-subtitle)]">{subtitle}</p>
            ) : null}
          </div>
          {isBootstrapLoading ? (
            <div className="bfrs-mt-[18px] bfrs-flex bfrs-gap-2">
              <Skeleton animated={false} variant="text" className="bfrs-h-7 bfrs-w-[118px] bfrs-rounded-pill bfrs-bg-surface-card" />
              <Skeleton animated={false} variant="text" className="bfrs-h-7 bfrs-w-[98px] bfrs-rounded-pill bfrs-bg-surface-card" />
            </div>
          ) : resolvedChips.length > 0 && (
            <div className="bfrs-mt-[18px] bfrs-flex bfrs-max-w-full bfrs-flex-wrap bfrs-justify-center bfrs-gap-2">
              {resolvedChips.map((chip, index) => {
                const isChipLabelMissing = isMissingDisplayNode(chip.label);

                if (isChipLabelMissing) {
                  return (
                    <Skeleton
                      key={index}
                      animated={false}
                      variant="text"
                      className={cn("bfrs-h-7 bfrs-rounded-pill bfrs-bg-surface-card", chipSkeletonWidthClassNames[index % chipSkeletonWidthClassNames.length])}
                    />
                  );
                }

                return (
                  <span
                    key={index}
                    className="bfrs-inline-flex bfrs-h-7 bfrs-max-w-[136px] bfrs-items-center bfrs-justify-center bfrs-gap-[5px] bfrs-rounded-pill bfrs-border bfrs-border-[var(--bfrs-dcl-color-neutral-stroke-2)] bfrs-bg-surface-card bfrs-px-[7px] bfrs-text-[11.667px] bfrs-font-medium bfrs-leading-none bfrs-text-[var(--bfrs-dcl-color-business-card-text)] bfrs-shadow-low"
                  >
                    {chip.icon && <span className="bfrs-text-primary-bright">{chip.icon}</span>}
                    <span className="bfrs-truncate">{chip.label}</span>
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <div className="bfrs-px-[44px] bfrs-pb-8 bfrs-pt-[8px]">
          {sections.length === 0 ? (
            <BusinessInfoDetailSkeletonRows rows={DEFAULT_BUSINESS_INFO_SKELETON_ROWS} />
          ) : sections.map((section, sectionIndex) => (
            <section key={sectionIndex} className={cn(sectionIndex > 0 && "bfrs-mt-4")}>
              {section.title && <h4 className="bfrs-mb-3 bfrs-text-[10px] bfrs-font-bold bfrs-uppercase bfrs-leading-normal bfrs-tracking-[0.07em] bfrs-text-text-disabled">{section.title}</h4>}
              <dl className="bfrs-grid bfrs-gap-[14px] bfrs-text-[11.667px] bfrs-leading-normal">
                {section.items.map((item, itemIndex) => {
                  const isItemValueMissing = isMissingDisplayNode(item.value);

                  return (
                    <div key={itemIndex} className="bfrs-grid bfrs-grid-cols-[minmax(0,92px)_auto_minmax(0,1fr)] bfrs-items-start bfrs-gap-x-6">
                      <dt className="bfrs-text-[var(--bfrs-dcl-color-business-card-muted)]">
                        {isItemValueMissing ? <Skeleton animated={false} variant="text" className="bfrs-h-4 bfrs-w-full" /> : item.label}
                      </dt>
                      <span aria-hidden="true" className="bfrs-text-[var(--bfrs-dcl-color-business-card-muted)]">
                        {isItemValueMissing ? <Skeleton animated={false} variant="text" className="bfrs-h-4 bfrs-w-1" /> : ":"}
                      </span>
                      <dd className="bfrs-min-w-0 bfrs-font-semibold bfrs-text-[var(--bfrs-dcl-color-business-card-text)]">
                        {isItemValueMissing ? (
                          <Skeleton
                            animated={false}
                            variant="text"
                            className={cn("bfrs-h-4", skeletonValueWidthClassNames[(sectionIndex + itemIndex) % skeletonValueWidthClassNames.length])}
                          />
                        ) : item.value}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </section>
          ))}
        </div>
      </div>
    );
  }
);

BusinessInfoDisplayCard.displayName = "BusinessInfoDisplayCard";
